import type * as React from "react";

import {
  autoPlacement,
  autoUpdate,
  flip,
  limitShift,
  type Middleware,
  offset,
  type Placement,
  shift,
  size,
  useFloating,
} from "@floating-ui/react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface IUseTooltipOptions {
  closeOnOutsideClick?: boolean;
  // Cap the tooltip height to the space available in the viewport (turning it into a flex column) so a
  // tall tooltip never overflows the screen — its content is expected to scroll within that height
  constrainToViewport?: boolean;
  defaultVisible?: boolean;
  delayHide?: number;
  delayShow?: number;
  // Honor the given placement exactly (disables flip/shift collision handling)
  fixedPlacement?: boolean;
  // Constrain flipping to the app layout element ([data-popper-boundary]) instead of the viewport
  flipToLayoutBoundary?: boolean;
  interactive?: boolean;
  // Popper-style [skidding, distance] offset from the trigger
  offset?: [number, number];
  onVisibleChange?: (visible: boolean) => void;
  placement?: TTooltipPlacement;
  // Match the tooltip width to the trigger width
  sameWidth?: boolean;
  tooltipClassName?: string;
  trigger?: TTooltipTrigger | TTooltipTrigger[];
  visible?: boolean;
}
// "auto" is translated to the autoPlacement() middleware (floating-ui dropped it as a placement string)
export type TTooltipPlacement = "auto" | Placement;

export type TTooltipTrigger = "click" | "focus" | "hover" | null;

const DEFAULT_OFFSET: [number, number] = [0, 8];
// Breathing room kept between a constrained tooltip and the viewport edges
const VIEWPORT_PADDING = 8;

export const useTooltip = (options: IUseTooltipOptions = {}) => {
  const {
    closeOnOutsideClick = true,
    constrainToViewport = false,
    defaultVisible = false,
    delayHide = 100,
    delayShow = 100,
    fixedPlacement = false,
    flipToLayoutBoundary = false,
    interactive = false,
    offset: offsetOption,
    onVisibleChange,
    placement = "top",
    sameWidth = false,
    tooltipClassName,
    trigger = "hover",
    visible: controlledVisible,
  } = options;

  const [uncontrolledVisible, setUncontrolledVisible] = useState(defaultVisible);
  const isControlled = controlledVisible !== undefined;
  const visible = isControlled ? controlledVisible : uncontrolledVisible;

  const isAutoPlacement = placement === "auto";
  const [skidding, distance] = offsetOption ?? DEFAULT_OFFSET;
  const middleware = useMemo(() => {
    // Middleware order matters: offset first, then flip/autoPlacement, then shift (per the floating-ui migration guide)
    const result: Middleware[] = [offset({ crossAxis: skidding, mainAxis: distance })];
    if (!fixedPlacement) {
      // Function form (derivable options) because the boundary element can mount after this hook, or remount
      const collisionOptions = () => ({
        boundary: flipToLayoutBoundary
          ? (document.querySelector("[data-popper-boundary]") ?? undefined)
          : undefined,
      });
      result.push(
        isAutoPlacement ? autoPlacement(collisionOptions) : flip(collisionOptions),
        shift({ limiter: limitShift() }),
      );
    }
    if (sameWidth || constrainToViewport) {
      // size runs last so it measures the space left after flip/shift have chosen the final placement
      result.push(
        size({
          apply: ({ availableHeight, elements, rects }) => {
            if (sameWidth) elements.floating.style.width = `${rects.reference.width}px`;
            if (constrainToViewport) {
              // Cap to the available space and become a flex column so the content can scroll within it
              Object.assign(elements.floating.style, {
                display: "flex",
                flexDirection: "column",
                maxHeight: `${availableHeight}px`,
              });
            }
          },
          padding: VIEWPORT_PADDING,
        }),
      );
    }
    return result;
  }, [constrainToViewport, distance, fixedPlacement, flipToLayoutBoundary, isAutoPlacement, sameWidth, skidding]);

  const { elements, floatingStyles, refs } = useFloating<HTMLElement>({
    middleware,
    open: visible,
    placement: isAutoPlacement ? undefined : placement,
    whileElementsMounted: autoUpdate,
  });

  const triggerRef = elements.reference;
  const tooltipRef = elements.floating;

  const latestRef = useRef({ isControlled, onVisibleChange, tooltipRef, triggerRef, visible });
  latestRef.current = { isControlled, onVisibleChange, tooltipRef, triggerRef, visible };

  const setVisible = useCallback((value: boolean) => {
    if (!latestRef.current.isControlled) setUncontrolledVisible(value);
    latestRef.current.onVisibleChange?.(value);
  }, []);

  const timerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const hideTooltip = useCallback(() => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setVisible(false), delayHide);
  }, [delayHide, setVisible]);

  const showTooltip = useCallback(() => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setVisible(true), delayShow);
  }, [delayShow, setVisible]);

  const toggleTooltip = useCallback(() => {
    if (latestRef.current.visible) {
      hideTooltip();
    } else {
      showTooltip();
    }
  }, [hideTooltip, showTooltip]);

  // Content key: a new array with the same triggers must not re-bind listeners, and deps must stay fixed-size
  const triggerKey = Array.isArray(trigger) ? trigger.join(",") : String(trigger);
  const isTriggeredBy = useCallback(
    (triggerType: TTooltipTrigger) => (Array.isArray(trigger) ? trigger.includes(triggerType) : trigger === triggerType),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- triggerKey captures trigger's content
    [triggerKey],
  );

  useEffect(() => {
    if (!visible || !closeOnOutsideClick) return;
    const handleClickOutside = (event: MouseEvent) => {
      const { tooltipRef, triggerRef } = latestRef.current;
      const target = event.composedPath?.()?.[0] || event.target;
      if (target instanceof Node && tooltipRef && triggerRef && !tooltipRef.contains(target) && !triggerRef.contains(target)) {
        hideTooltip();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeOnOutsideClick, hideTooltip, visible]);

  useEffect(() => {
    if (!triggerRef || !isTriggeredBy("click")) return;
    triggerRef.addEventListener("click", toggleTooltip);
    return () => triggerRef.removeEventListener("click", toggleTooltip);
  }, [triggerRef, isTriggeredBy, toggleTooltip]);

  useEffect(() => {
    if (!triggerRef || !isTriggeredBy("focus")) return;
    triggerRef.addEventListener("focus", showTooltip);
    triggerRef.addEventListener("blur", hideTooltip);
    return () => {
      triggerRef.removeEventListener("focus", showTooltip);
      triggerRef.removeEventListener("blur", hideTooltip);
    };
  }, [triggerRef, isTriggeredBy, showTooltip, hideTooltip]);

  useEffect(() => {
    if (!triggerRef || !isTriggeredBy("hover")) return;
    triggerRef.addEventListener("mouseenter", showTooltip);
    triggerRef.addEventListener("mouseleave", hideTooltip);
    return () => {
      triggerRef.removeEventListener("mouseenter", showTooltip);
      triggerRef.removeEventListener("mouseleave", hideTooltip);
    };
  }, [triggerRef, isTriggeredBy, showTooltip, hideTooltip]);

  // Keep the tooltip open while hovering it (interactive tooltips only)
  useEffect(() => {
    if (!tooltipRef || !interactive || !isTriggeredBy("hover")) return;
    tooltipRef.addEventListener("mouseenter", showTooltip);
    tooltipRef.addEventListener("mouseleave", hideTooltip);
    return () => {
      tooltipRef.removeEventListener("mouseenter", showTooltip);
      tooltipRef.removeEventListener("mouseleave", hideTooltip);
    };
  }, [tooltipRef, interactive, isTriggeredBy, showTooltip, hideTooltip]);

  const getTooltipProps = useCallback(
    (): { className?: string; style: React.CSSProperties } => ({
      className: tooltipClassName,
      style: floatingStyles,
    }),
    [floatingStyles, tooltipClassName],
  );

  const tooltipProps = useMemo(() => getTooltipProps(), [getTooltipProps]);

  return {
    getTooltipProps, // The props for the tooltip, required to use the object or the function
    setTooltipRef: refs.setFloating, // Set the tooltip ref required to use (as ref or ref)
    setTriggerRef: refs.setReference, // Set the trigger ref required to use (as ref or ref)
    tooltipProps, // The props for the tooltip, required to use the object or the function
    tooltipRef, // The tooltip element, used e.g. for outside-click detection
    triggerRef, // The trigger element, used e.g. for outside-click detection
    visible, // The tooltip visibility, required to use for hover tooltip
  };
};
